import { useCallback, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { IoIosClose } from 'react-icons/io';

import Button from 'components/ui/button';
import { useModal } from 'context/ModalContext';
import Box from 'components/ui/box';
import { actionLog } from 'services/actionlog.service';

const useFocusTrap = (ref: React.RefObject<HTMLDivElement>, isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !ref.current) return;
      event.preventDefault();

      const focusableElements = ref.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const focusableArray = Array.from(focusableElements) as HTMLElement[];
      const currentIndex = focusableArray.indexOf(document.activeElement as HTMLElement);

      let nextIndex: number;
      if (event.shiftKey) {
        nextIndex = currentIndex <= 0 ? focusableArray.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= focusableArray.length - 1 ? 0 : currentIndex + 1;
      }

      focusableArray[nextIndex]?.focus();
    };

    // Aguarda o DOM ser renderizado antes de focar
    const timeoutId = setTimeout(() => {
      if (ref.current) {
        const firstFocusable = ref.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        firstFocusable?.focus();
      }
    }, 50);

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, ref, onClose]);
};

interface Props {
  callback: (obj: { id: string; message?: string }) => void;
  id: string;
  playerInfo: any;
  data?: any;
}

export default function ModalStopQuiz(props: Props) {
  ReactModal.setAppElement('#modal');

  const modalID = 'validation-stop-quiz';

  const { openModal, modalData, closeModal } = useModal();
  const { callback, id, playerInfo, data } = props;

  const [modalIsOpen, setIsOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const handleCloseModal = useCallback(() => {
    if (!loading) closeModal();
  }, [closeModal, loading]);

  useFocusTrap(modalContentRef, modalIsOpen, handleCloseModal);

  async function send() {
    if (!playerInfo?._id) return;

    setLoading(true);

    await actionLog(playerInfo.chave, 'answer_quiz', {
      taskId: data._id,
      answered: false,
    });

    callback({ id: 'stop_quiz' });
    closeModal();
    setLoading(false);
  }

  function afterOpenModal() {
    //
  }

  useEffect(() => {
    if (typeof id === 'string' && typeof openModal === 'string' && openModal === `${modalID}-${id}`) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [id, openModal]);

  if (!modalIsOpen) return <></>;

  return (
    <>
      <ReactModal
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={true}
        isOpen={modalIsOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={handleCloseModal}
        contentLabel="Avaliação"
        portalClassName="relative z-50"
        overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-60 flex flex-col items-center w-screen h-screen p-6 pt-[10vh] overflow-y-auto"
        contentElement={() => (
          <div ref={modalContentRef} role="dialog" aria-modal="true" aria-labelledby="stop-quiz-modal-title">
            <Box className={`w-[90%] lg:w-auto lg:max-w-[600px]`}>
              <Button
                color="primary"
                className="absolute -right-5 -top-5 rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center"
                onClick={handleCloseModal}
                aria-label="Fechar modal"
              >
                <IoIosClose color="white" size={40} />
              </Button>

            <div className="w-full lg:w-[536px] flex flex-col items-center justify-center space-y-8">
              <h4 id="stop-quiz-modal-title" className="block text-center text-yellow text-lg font-semibold leading-none">Matriz de orientação</h4>
              <p className="text-alice-blue text-base font-thin block">
                Ao negar a possibilidade de apreciar a realização pela orientação da matriz, essa ação não poderá ser desfeita.
                Deseja confirmar essa ação?
              </p>
              <button
                className={`text-white bg-slate-blue text-sm inline-flex items-center justify-center font-semibold uppercase rounded py-3 px-4 leading-none`}
                onClick={() => {
                  send();
                }}
              >
                CONFIRMAR
              </button>
            </div>
            </Box>
          </div>
        )}
      />
    </>
  );
}
